import { Suspense, lazy, type JSX, type ErrorInfo } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import * as Sentry from '@sentry/react';
import { AuthProvider } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ErrorFallback from './components/ErrorFallback';
import ProtectedRoute from './components/ProtectedRoute';
import Gallery from './pages/Gallery';
import AboutMe from './pages/AboutMe';
import ContactMe from './pages/ContactMe';
import Courses from './pages/Courses';
import ItemPage from './pages/ItemPage';

// Sign-in and the admin tools behind it are never touched by the vast
// majority of visitors — splitting them into their own chunks keeps that
// code (and the extra bytes it pulls in, e.g. fflate for the backup zip)
// off the critical path for a first-time, anonymous gallery visit.
const Login = lazy(() => import('./pages/Login'));
const Upload = lazy(() => import('./pages/admin/Upload'));
const Edit = lazy(() => import('./pages/admin/Edit'));
const Backup = lazy(() => import('./pages/admin/Backup'));

export default function App(): JSX.Element {
  return (
    <BrowserRouter>
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={(error: unknown, info: ErrorInfo) => {
          Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
        }}
        onReset={() => {
          window.location.assign('/');
        }}
      >
        <AuthProvider>
          {/* Decorative fixed background, restored from the pre-rewrite site (removed
              there by accident) — "hovers" in place while the page scrolls past it,
              sitting behind everything else. */}
          <div
            className="fixed right-0 top-10 z-[-10] h-full w-full bg-cover bg-no-repeat sm:w-2/3 lg:w-2/6"
            style={{ backgroundImage: "url('/peacock-bg.webp')" }}
          />
          <Navbar />
          {/* bg-white/70 lets the peacock show through in empty space while
              keeping text legible where it would otherwise sit directly over
              the artwork — same reasoning the pre-rewrite site's #main-content
              wrapper had, without it text became unreadable in a few spots. */}
          <div className="min-h-screen bg-white/70">
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<Gallery />} />
                <Route path="/aboutMe" element={<AboutMe />} />
                <Route path="/contactMe" element={<ContactMe />} />
                <Route path="/courses" element={<Courses />} />
                <Route path="/painting/:id" element={<ItemPage />} />
                <Route path="/login" element={<Login />} />
                <Route
                  path="/admin/upload"
                  element={
                    <ProtectedRoute>
                      <Upload />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/edit/:id"
                  element={
                    <ProtectedRoute>
                      <Edit />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/backup"
                  element={
                    <ProtectedRoute>
                      <Backup />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Suspense>
          </div>
          <Footer />
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
